import { useAuth } from '#globalContexts/AuthProvider';
import { useCapabilityCheck } from '#globalHooks/useCapabilityCheck';

import { ADDON_CONFIGS } from './addonConfigs';

const MS_PER_DAY = 86_400_000;

/**
 * Generic single source of UI truth for any per-org add-on's trial / paid state.
 *
 * ACCESS is decided by the JWT capability (`hasAccess` ← useCapabilityCheck(`<addonKey>.access`)),
 * never by the orgInfo fields — those drive PRESENTATION only (countdown, CTA). Gating `isOnTrial`
 * on the access source (not the capability) avoids a banner flicker right after starting the add-on
 * while the JWT refreshes.
 *
 * @param {string} addonKey - e.g. 'crm'
 * @returns {{
 *   isLoading: boolean,
 *   hasAccess: boolean,
 *   trialSource: ('demo'|'addon'|null),
 *   isOnTrial: boolean,
 *   addonActive: boolean,
 *   daysRemaining: (number|null),
 *   isExpired: boolean,
 *   trialUsed: boolean,
 *   canStartTrial: boolean,
 *   canActivate: boolean,
 *   cancelAtPeriodEnd: boolean,
 *   trialEndsAt: (string|null),
 *   accessEndsAt: (string|null),
 *   hasTrial: boolean,
 * }}
 */
export function useAddonTrial(addonKey) {
	const { orgInfo, orgPlan, subscription } = useAuth();
	const { hasCapability: hasAccess, isLoading: capLoading } = useCapabilityCheck(`${addonKey}.access`);
	const config = ADDON_CONFIGS[addonKey];
	const hasTrial = (config?.trialDays ?? 0) > 0;
	const proIncluded = Boolean(config?.proComp);

	const state = orgInfo?.addons?.[addonKey] ?? null;
	const accessSource = state?.accessSource ?? null;
	const trialEndsAt = state?.trialEndsAt ?? null;
	const trialUsed = Boolean(state?.trialUsed);

	const isLoading = capLoading || !orgInfo;

	const trialEndMs = trialEndsAt ? new Date(trialEndsAt).getTime() : null;
	const validTrialEnd = trialEndMs !== null && !Number.isNaN(trialEndMs);

	// Ceil so "23h left" reads as "1 Tag", not "0".
	const daysRemaining = validTrialEnd ? Math.max(0, Math.ceil((trialEndMs - Date.now()) / MS_PER_DAY)) : null;
	const isExpired = validTrialEnd ? Date.now() > trialEndMs : false;

	const trialSource = accessSource === 'demo_trial' ? 'demo' : accessSource === 'addon_trial' ? 'addon' : null;
	const isOnTrial = (accessSource === 'demo_trial' || accessSource === 'addon_trial') && !isExpired;
	const addonActive = accessSource === 'addon_active';

	// Eligible for a FREE 14-day trial: anyone who hasn't used it yet and has no access — demo AND
	// paying plans alike (the trial never touches Stripe, so no subscription is required to start it).
	// pro is excluded (CRM is comped); cancelled must reactivate and paused must resume their base
	// subscription first.
	const canStartTrial =
		hasTrial &&
		!hasAccess &&
		!trialUsed &&
		accessSource === null &&
		!(proIncluded && orgPlan === 'pro') &&
		orgPlan !== 'cancelled' &&
		orgPlan !== 'paused';

	// Can activate as PAID after the trial is used — requires an active paid subscription to attach
	// the Stripe item. Demo has no subscription → it upgrades (buys a plan) instead, not "activate";
	// paused keeps its subscription but add-on items are blocked until it resumes.
	const isPaidPlan =
		Boolean(subscription) &&
		orgPlan !== 'demo' &&
		orgPlan !== 'cancelled' &&
		orgPlan !== 'paused' &&
		!(proIncluded && orgPlan === 'pro');
	// An add-on without trial is activated as paid right away.
	const canActivate = isPaidPlan && !hasAccess && (trialUsed || !hasTrial);

	return {
		isLoading,
		hasAccess,
		trialSource,
		isOnTrial,
		addonActive,
		daysRemaining,
		isExpired,
		trialUsed,
		canStartTrial,
		canActivate,
		cancelAtPeriodEnd: Boolean(state?.cancelAtPeriodEnd),
		trialEndsAt,
		// When a cancelled add-on loses access: a trial runs out on its own date, a paid one is
		// removed from the subscription at the next billing boundary.
		accessEndsAt: addonActive ? (subscription?.currentPeriodEnd ?? null) : trialEndsAt,
		hasTrial,
	};
}
